import { Component, input, output, signal, computed, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DropdownItem } from '../dropdown/dropdown.component';
import { CurrencySet } from '@gs-2.0/core';

/**
 * Interface for the compound amount and currency value
 */
export interface AmountCurrency {
  amount: number | null;
    currency: CurrencySet | null;
}

/**
 * Amount Currency Field Component
 * A compound form control that combines an amount input with currency formatting
 * and a currency dropdown selector into a single ControlValueAccessor.
 *
 * Implements ControlValueAccessor for Angular forms integration.
 * Returns a compound value: { amount: number | null, currency: string }
 */
@Component({
  selector: 'app-amount-currency-field',
  standalone: false,
  templateUrl: './amount-currency-field.component.html',
  styleUrls: ['./amount-currency-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmountCurrencyFieldComponent),
      multi: true
    }
  ]
})
export class AmountCurrencyFieldComponent implements ControlValueAccessor {
  /**
   * Label for the amount field
   */
  label = input<string>('');

  /**
   * Placeholder for the amount field
   */
  placeholder = input<string>('');

  /**
   * Placeholder for the currency dropdown
   */
  currencyPlaceholder = input<string>('Currency');

  /**
   * Whether the field is required
   */
  required = input<boolean>(false);

  /**
   * Whether the field is disabled (for template-driven forms)
   */
  disabled = input<boolean>(false);

  /**
   * Array of available currencies for the dropdown
   */
  currencyItems = input<DropdownItem[]>([]);

  /**
   * Whether the currency dropdown specifically should be disabled
   * Different from the overall disabled state which affects both amount and currency
   */
  currencyDisabled = input<boolean>(false);

  /**
   * Whether the field has a validation error
   */
  hasError = input<boolean>(false);

  /**
   * The error message to display
   */
  errorMessage = input<string>('');

  /**
   * Emits when the compound value changes
   */
  valueChange = output<AmountCurrency | null>();

  /**
   * Internal amount value
   */
  private internalAmount = signal<number | null>(null);

  /**
   * Internal currency value
   */
  private internalCurrency = signal<CurrencySet | null>(null);

  /**
   * Internal touched state
   */
  private isTouched = signal<boolean>(false);

  /**
   * Internal disabled state (set by reactive forms via setDisabledState)
   */
  private isDisabledByForm = signal<boolean>(false);

  /**
   * Computed disabled state (combines input signal and form control state)
   */
  isDisabled = computed(() => this.isDisabledByForm() || this.disabled());

  /**
   * Computed state for currency-specific disabled state
   * Combines overall disabled state with currency-specific disabled
   */
  isCurrencyDisabled = computed(() => this.isDisabled() || this.currencyDisabled());

  /**
   * Computed error display state
   * Shows error only when field has error and is touched
   */
  showError = computed(() => this.hasError() && this.isTouched());

  /**
   * Get currency label for display in search field
   */
  getCurrencyLabel = computed(() => {
    const currencyId = this.internalCurrency()?.id || '';
    const items = this.currencyItems();
    const found = items.find(item => item.id === currencyId);
    return found ? found.label : '';
  });

  /**
   * onChange callback for ControlValueAccessor
   */
  private onChange: (value: AmountCurrency | null) => void = () => {};

  /**
   * onTouched callback for ControlValueAccessor
   */
  private onTouched: () => void = () => {};

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle amount field value change
   * @param value The string value from the input (formatted by directive)
   */
  onAmountChange(value: string): void {
    // Parse the numeric value (directive handles formatting)
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : null;
    this.internalAmount.set(numericValue);
    this.emitCompoundValue();
  }

  /**
   * Handle amount field blur
   */
  onAmountBlur(): void {
    this.markAsTouched();
  }

  /**
   * Handle currency selection from dropdown
   * @param item Selected currency item
   */
  onCurrencySelected(item: DropdownItem): void {
    this.internalCurrency.set(null);
    this.markAsTouched();
    this.emitCompoundValue();
  }

  /**
   * Handle currency value change (e.g., when cleared)
   * @param value The new value
   */
  onCurrencyValueChange(value: string): void {
    if (!value) {
      this.internalCurrency.set(null);
      this.emitCompoundValue();
    }
  }

  /**
   * Handle currency field blur
   */
  onCurrencyBlurred(): void {
    this.markAsTouched();
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Emit the compound value to parent form control and output
   */
  private emitCompoundValue(): void {
    const value: AmountCurrency = {
      amount: this.internalAmount(),
      currency: this.internalCurrency()
    };
    this.onChange(value);
    this.valueChange.emit(value);
  }

  /**
   * Mark the control as touched
   */
  private markAsTouched(): void {
    if (!this.isTouched()) {
      this.isTouched.set(true);
      this.onTouched();
    }
  }

  // ============================================================================
  // CONTROLVALUEACCESSOR IMPLEMENTATION
  // ============================================================================

  /**
   * Write a new value to the component
   * @param value The compound value from form control
   */
  writeValue(value: AmountCurrency | null): void {
    if (value) {
    this.internalAmount.set(value.amount);
      this.internalCurrency.set(null);
    } else {
      // Only clear amount, preserve currency to prevent accidental clearing
      // The currency should only be cleared via explicit user action
      this.internalAmount.set(null);
      // Don't clear currency: this.internalCurrency.set('');
    }
  }

  /**
   * Register onChange callback
   * @param fn The callback function
   */
  registerOnChange(fn: (value: AmountCurrency | null) => void): void {
    this.onChange = fn;
  }

  /**
   * Register onTouched callback
   * @param fn The callback function
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Set the disabled state
   * @param isDisabled Whether the control is disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabledByForm.set(isDisabled);
  }
}
